import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Stack
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../../services/api';

const EditAerialInspectionModal = ({ open, onClose, inspection, onUpdated }) => {
  const [formData, setFormData] = useState({
    title: '',
    notes: '',
    inspection_date: null,
    status: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (inspection) {
      setFormData({
        title: inspection.title || '',
        notes: inspection.notes || '',
        inspection_date: inspection.inspection_date ? new Date(inspection.inspection_date) : null,
        status: inspection.status || '',
      });
    }
  }, [inspection]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.inspection_date) newErrors.inspection_date = 'Inspection date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const updateData = {
        ...formData,
        inspection_date: formData.inspection_date.toISOString().split('T')[0], // Format as YYYY-MM-DD
      };
      
      const response = await api.put(`/aerial-inspections/${inspection.id}`, updateData);

      if (response.status === 200) {
        onUpdated();
        onClose();
      } else {
        setError(response.data.message || 'Failed to update inspection.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while updating the inspection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EditIcon />
            Edit Aerial Inspection
          </Stack>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Inspection Title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                label="Inspection Date"
                value={formData.inspection_date}
                onChange={(newValue) => handleChange('inspection_date', newValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    error={!!errors.inspection_date} 
                    helperText={errors.inspection_date} 
                  />
                )}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditAerialInspectionModal;